import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ForgotPasswordData } from '../types';

const ForgotPassword: React.FC = () => {
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordData>();

  const onSubmit = async (data: ForgotPasswordData) => {
    try {
      setError('');
      setLoading(true);
      await api.post('/auth/forgot-password', data);
      setSent(true);
    } catch (err: any) {
      // Backend returns generic success; only surface unexpected errors
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden gradient-login">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-md w-full mx-4 relative z-10">
        <h2 className="text-2xl font-bold text-center text-primary-800 mb-1" style={{ letterSpacing: '-0.02em' }}>
          Forgot your password?
        </h2>
        <p className="text-center text-primary-700 text-base font-medium opacity-80 mb-7">
          We'll email you a link to reset it
        </p>

        <div className="bg-white rounded-3xl p-6 border shadow-blue-lg" style={{ borderColor: 'rgba(191, 219, 254, 0.5)' }}>
          {sent ? (
            <div className="text-center space-y-4">
              <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                If an account with that email exists, a password reset link has been sent. Please check your inbox.
              </div>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full h-[52px] gradient-button text-white font-semibold"
                style={{ borderRadius: '14px' }}
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="rounded-md bg-danger-bg border border-danger-light p-3">
                  <div className="text-sm text-danger-dark font-medium">{error}</div>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1.5">Email address</label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' },
                  })}
                  type="email"
                  className="w-full px-3 h-[52px] bg-white/60 text-base text-gray-900 placeholder-gray-400 focus:outline-none"
                  style={{ borderWidth: '1.5px', borderColor: '#BFDBFE', borderRadius: '12px' }}
                  placeholder="you@careplanpro.com"
                />
                {errors.email && <p className="mt-1.5 text-sm text-danger-dark font-medium">{errors.email.message}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-[52px] gradient-button text-white font-semibold text-md disabled:opacity-50"
                style={{ borderRadius: '14px', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.5)' }}
              >
                {loading ? 'Sending...' : 'Send reset link'}
              </button>

              <div className="flex justify-center pt-1">
                <button type="button" onClick={() => navigate('/login')} className="text-base text-primary-600 font-semibold">
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
